import React, { useState, useRef, useEffect } from "react";
import { View, Text, ScrollView, ImageBackground, Pressable, TouchableOpacity, Animated } from "react-native";
import { useNavigation } from "@react-navigation/native";
import styles from "../Styles/LandingStyles";
import Popup from "../Widgets/Popup";
import { useUser } from "../Widgets/UserContext";
import ComingSoon from "./ComingSoon";
import { StyleSheet, Dimensions } from "react-native";
import SEOHead from "../components/SEOHead";

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isSmallMobile = width <= 430; // ~393 breakpoint (iPhone 14 Pro and smaller)
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1440;
const isLargeDesktop = width > 1440;

const Landing = () => {
	const [isBlurred, setIsBlurred] = React.useState(false);
	const [popupType, setPopupType] = React.useState(null);

	// Set to true to show Coming Soon Screen
	const [isComingSoon, setIsComingSoon] = React.useState(false);
	const { currentUser, currentUserData, setCurrentUser, setCurrentUserData } = useUser();
	// console.warn(currentUserData);

	const navigation = useNavigation();

	// State for resources overlay on mobile
	const [showResourcesOverlay, setShowResourcesOverlay] = useState(false);

	// Animation value for overlay
	const overlayAnim = useRef(new Animated.Value(0)).current;

	// Animate overlay in when it becomes visible
	useEffect(() => {
		if (showResourcesOverlay) {
			Animated.timing(overlayAnim, {
				toValue: 1,
				duration: 300,
				useNativeDriver: true,
			}).start();
		}
	}, [showResourcesOverlay]);

	// Toggle resources overlay for mobile
	const toggleResourcesOverlay = () => {
		if (isMobile) {
			if (!showResourcesOverlay) {
				setShowResourcesOverlay(true);
				// Animation will be handled in the useEffect
			} else {
				// Animate out before hiding
				Animated.timing(overlayAnim, {
					toValue: 0,
					duration: 300,
					useNativeDriver: true,
				}).start(() => {
					setShowResourcesOverlay(false);
					// Reset animation value for next time
					overlayAnim.setValue(0);
				});
			}
		} else {
			// Use the desktop panel for non-mobile
			toggleDesktopResourcesPanel();
		}
	};

	const fadeAnim = {
		header: useRef(new Animated.Value(0)).current,
		content: useRef(new Animated.Value(0)).current,
		resources: useRef(new Animated.Value(0)).current,
		mobileButton: useRef(new Animated.Value(0)).current
	};

	const slideAnim = {
		header: useRef(new Animated.Value(-8)).current,
		content: useRef(new Animated.Value(-50)).current,
		resources: useRef(new Animated.Value(30)).current,
		mobileButton: useRef(new Animated.Value(-50)).current
	};

	// Background movement animation
	const bgScaleAnim = useRef(new Animated.Value(1)).current;
	const bgTranslateX = useRef(new Animated.Value(0)).current;
	const bgTranslateY = useRef(new Animated.Value(0)).current;

	// Social link hover states
	const [hoveredSocial, setHoveredSocial] = useState(null);
	const [hoveredResource, setHoveredResource] = useState(false);

	useEffect(() => {
		const animations = [
			// Content area animations (first)
			Animated.parallel([
				Animated.timing(fadeAnim.content, {
					toValue: 1,
					duration: 800,
					useNativeDriver: true,
				}),
				Animated.timing(slideAnim.content, {
					toValue: 0,
					duration: 800,
					useNativeDriver: true,
				}),
			]),
			// Header animations (second)
			Animated.parallel([
				Animated.timing(fadeAnim.header, {
					toValue: 1,
					duration: 800,
					useNativeDriver: true,
				}),
				Animated.timing(slideAnim.header, {
					toValue: 0,
					duration: 800,
					useNativeDriver: true,
				}),
			]),
			// Mobile button animations (third)
			Animated.parallel([
				Animated.timing(fadeAnim.mobileButton, {
					toValue: 1,
					duration: 800,
					useNativeDriver: true,
				}),
				Animated.timing(slideAnim.mobileButton, {
					toValue: 0,
					duration: 800,
					useNativeDriver: true,
				}),
			]),
			// Resources ribbon animations (much later)
			Animated.parallel([
				Animated.timing(fadeAnim.resources, {
					toValue: 1,
					duration: 800,
					useNativeDriver: true,
				}),
				Animated.timing(slideAnim.resources, {
					toValue: 0,
					duration: 800,
					useNativeDriver: true,
				}),
			]),
		];

		Animated.stagger(600, animations).start();

		// Subtle background movement animation (Ken Burns effect)
		const startBackgroundAnimation = () => {
			Animated.loop(
				Animated.sequence([
					Animated.parallel([
						Animated.timing(bgScaleAnim, {
							toValue: 1.08,
							duration: 20000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateX, {
							toValue: -15,
							duration: 20000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateY, {
							toValue: -10,
							duration: 20000,
							useNativeDriver: true,
						}),
					]),
					Animated.parallel([
						Animated.timing(bgScaleAnim, {
							toValue: 1.04,
							duration: 18000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateX, {
							toValue: 10,
							duration: 18000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateY, {
							toValue: 5,
							duration: 18000,
							useNativeDriver: true,
						}),
					]),
					Animated.parallel([
						Animated.timing(bgScaleAnim, {
							toValue: 1,
							duration: 16000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateX, {
							toValue: 0,
							duration: 16000,
							useNativeDriver: true,
						}),
						Animated.timing(bgTranslateY, {
							toValue: 0,
							duration: 16000,
							useNativeDriver: true,
						}),
					]),
				])
			).start();
		};
		
		// Start background animation after a short delay
		setTimeout(startBackgroundAnimation, 500);
	}, []);

	React.useEffect(() => {
		// Brand-new users answer the referral question and start the tutorial before they see the Hub.
		// Landing stays mounted underneath other screens, so only redirect while it is the visible screen.
		if (currentUserData && !currentUserData.referralPending && navigation.isFocused()) {
			navigation.navigate("Hub");
		}
	}, [currentUserData, navigation]);

	const navigateToChecklist = (type) => {
		navigation.navigate("Checklist", { type });
	};

	const navigateToCatalogue = () => {
		navigation.navigate("Catalogue", { isFromLandingPage: true });
	};

	// Navigation
	const handleLinkPress = (pageName, popupType = null) => {
		// For mobile devices, directly show popup instead of navigating
		if (isMobile && pageName === "Register") {
			setIsBlurred(true);
			setPopupType('register');
			return;
		}
		
		if (popupType) {
			setIsBlurred(true);
			setPopupType(popupType);
		} else {
			if (isBlurred) {
				setIsBlurred(false); // Close any currently open popup
			} else {
				toggleBlur(pageName.toLowerCase()); // 'Register' will become 'register', 'Login' will become 'login'
			}
		}
	};

	// Blur
	const toggleBlur = (type = null) => {
		setIsBlurred(!isBlurred); // Toggle the blur
		setPopupType(type); // Set the popup type
	};

	const slideAnimDorm = React.useRef(new Animated.Value(-100)).current;
	const slideAnimApartment = React.useRef(new Animated.Value(-200)).current;
	const slideAnimGraduation = React.useRef(new Animated.Value(-300)).current;
	const slideAnimCatalogue = React.useRef(new Animated.Value(-400)).current;
	const slideAnimPrivacy = React.useRef(new Animated.Value(-400)).current;
	const slideAnimTerms = React.useRef(new Animated.Value(-400)).current;
	const slideAnimBlog = React.useRef(new Animated.Value(-400)).current;
	const slideAnimTutorials = React.useRef(new Animated.Value(-400)).current;
	const slideAnimWhyMyTassel = React.useRef(new Animated.Value(-400)).current;
	const slideAnimChecklist = React.useRef(new Animated.Value(-400)).current;
	const slideAnimContactUs = React.useRef(new Animated.Value(-400)).current;
	const [isMouseNearTabs, setIsMouseNearTabs] = useState(false);

	const slideIn = () => {
		setIsMouseNearTabs(true);
		Animated.timing(slideAnimWhyMyTassel, {
			toValue: 300,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimTutorials, {
			toValue: 250,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimBlog, {
			toValue: 200,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimChecklist, {
			toValue: 150,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimCatalogue, {
			toValue: 100,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimPrivacy, {
			toValue: 50,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimTerms, {
			toValue: 0,
			duration: 500,
			useNativeDriver: true,
		}).start();
		Animated.timing(slideAnimContactUs, {
			toValue: -50,
			duration: 500,
			useNativeDriver: true,
		}).start();
	};

	const slideOut = () => {
		setTimeout(() => {
			if (!isMouseNearTabs) {
				Animated.timing(slideAnimWhyMyTassel, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimDorm, {
					toValue: -100,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimApartment, {
					toValue: -200,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimChecklist, {
					toValue: -250,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimGraduation, {
					toValue: -300,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimCatalogue, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimPrivacy, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimTerms, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimBlog, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimTutorials, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
				Animated.timing(slideAnimContactUs, {
					toValue: -400,
					duration: 500,
					useNativeDriver: true,
				}).start();
			}
		}, 200); // Delay to allow mouse movement
	};

	// Animation values for buttons
	const scaleAnim = React.useRef(new Animated.Value(1)).current;

	const handlePressIn = () => {
		Animated.spring(scaleAnim, {
			toValue: 0.95,
			useNativeDriver: true,
		}).start();
	};

	const handlePressOut = () => {
		Animated.spring(scaleAnim, {
			toValue: 1,
			useNativeDriver: true,
		}).start();
	};

	const [hoveredButton, setHoveredButton] = useState(null); // Initialize state
	const [buttonPressed, setButtonPressed] = useState(false); // Add state for button press

	// Add desktop resources panel states and animations
	const [showDesktopResourcesPanel, setShowDesktopResourcesPanel] = useState(false);
	const desktopPanelAnim = useRef(new Animated.Value(300)).current;
	
	// Toggle desktop resources panel
	const toggleDesktopResourcesPanel = () => {
		if (!showDesktopResourcesPanel) {
			setShowDesktopResourcesPanel(true);
			Animated.timing(desktopPanelAnim, {
				toValue: 0,
				duration: 300,
				useNativeDriver: true,
			}).start();
		} else {
			Animated.timing(desktopPanelAnim, {
				toValue: 300,
				duration: 300,
				useNativeDriver: true,
			}).start(() => {
				setShowDesktopResourcesPanel(false);
			});
		}
	};

	return (
		<View style={isBlurred ? styles.blurredContainer : null}>
			<SEOHead
				title="My Tassel - Your Graduation Planning Solution"
				description="Create free digital graduation announcements, registries, and share events with MyTassel's all-in-one graduation planning platform."
			/>
			{isComingSoon && <ComingSoon />}
			{/* Mobile-only Get Started and What is MYTassel? buttons (side by side) */}
			{isMobile && (
				<Animated.View
					style={[
						{
							position: 'absolute',
							left: '5%',
							bottom: isSmallMobile ? '16%' : '22%',
							zIndex: 2,
							opacity: isBlurred ? 0 : fadeAnim.mobileButton,
							transform: [{ translateX: slideAnim.mobileButton }],
							pointerEvents: isBlurred ? 'none' : 'auto',
							width: '90%',
						}
					]}
				>
					<View style={{ width: '100%', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
						<Pressable
							style={[
								styles.getStartedButton,
								{ paddingHorizontal: 10, width: '48%' },
								hoveredButton === 'getStarted' && styles.getStartedButtonHover
							]}
							onMouseEnter={() => setHoveredButton('getStarted')}
							onMouseLeave={() => setHoveredButton(null)}
							onPress={() => handleLinkPress("Register")}
							onPressIn={handlePressIn}
							onPressOut={handlePressOut}
							activeOpacity={0.8}
						>
							<Text style={styles.buttonText}>Get Started</Text>
						</Pressable>
						<Pressable
							style={[
								styles.getStartedButton,
								{ paddingHorizontal: 10, width: '48%' },
								hoveredButton === 'learnMore' && styles.getStartedButtonHover
							]}
							onMouseEnter={() => setHoveredButton('learnMore')}
							onMouseLeave={() => setHoveredButton(null)}
							onPress={() => navigation.navigate('WhyMyTassel')}
							onPressIn={handlePressIn}
							onPressOut={handlePressOut}
							activeOpacity={0.8}
						>
							<Text style={styles.buttonText}>What is MyTassel?</Text>
						</Pressable>
					</View>
				</Animated.View>
			)}
			<ScrollView style={styles.body}>
				<View style={styles.section}>
					{isBlurred && (
						<View style={[
							styles.popupContainer, 
							isMobile && {
								backgroundColor: 'rgba(0,0,0,0.8)',
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 99999,
								flex: 1,
								justifyContent: 'center',
								alignItems: 'center'
							}
						]}>
							<Popup style={styles.popup} type={popupType} onClose={toggleBlur} />
						</View>
					)}

					<Animated.View
						style={{
							position: 'absolute',
							top: -20,
							left: -20,
							right: -20,
							bottom: -20,
							transform: [
								{ scale: bgScaleAnim },
								{ translateX: bgTranslateX },
								{ translateY: bgTranslateY }
							]
						}}
					>
						<ImageBackground 
							source={require("../assets/Landing Background.png")} 
							style={[
								styles.backgroundImage,
								{ width: '100%', height: '100%' }
							]}
						/>
					</Animated.View>
					<View 
						style={[
							{ flex: 1, position: 'relative', zIndex: 1 },
							isMobile && {
								pointerEvents: 'box-none',
							}
						]}
					>
						{isBlurred && (
							<View style={styles.overlayBlur} />
						)}
						<Animated.View 
							style={[
								styles.headerBar,
								{
									opacity: fadeAnim.header,
									transform: [{ translateY: slideAnim.header }],
								}
							]}
						>
							<View style={styles.headerContentRow}>
								<Pressable style={[styles.logoArea, isTablet && { maxHeight: 32 }]} onPress={() => navigation.navigate("My Tassel - Landing")}> 
									<Text style={styles.logoText}>MYTASSEL</Text>
								</Pressable>

								{/* Grouped right side: What is MyTassel, Registration, Login Now */}
								<View style={styles.headerRightGroup}>
									{!isMobile && (
										<Pressable
											style={[
												styles.registerButton,
												isTablet && { width: 120, height: 32, padding: 4 },
												!isMobile && { marginRight: 0 },
												hoveredButton === 'learnMoreHeader' && styles.registerButtonHover
											]}
											onMouseEnter={() => setHoveredButton('learnMoreHeader')}
											onMouseLeave={() => setHoveredButton(null)}
											onPress={() => navigation.navigate('WhyMyTassel')}
										>
											<Text style={styles.registerButtonText}>What is MyTassel?</Text>
										</Pressable>
									)}
									<Pressable
										style={[
											styles.registerButton,
											isTablet && { width: 120, height: 32, padding: 4 },
											hoveredButton === 'register' && styles.registerButtonHover
										]}
										onMouseEnter={() => setHoveredButton('register')}
										onMouseLeave={() => setHoveredButton(null)}
										onPress={() => handleLinkPress("Register")}
									>
										<Text style={styles.registerButtonText}>Registration</Text>
									</Pressable>
									<Pressable
										style={[
											styles.loginButton,
											hoveredButton === 'login' && styles.loginButtonHover
										]}
										onMouseEnter={() => setHoveredButton('login')}
										onMouseLeave={() => setHoveredButton(null)}
										onPress={() => handleLinkPress("Login")}
									>
										<Text style={styles.loginButtonText}>Login Now</Text>
									</Pressable>
								</View>
							</View>
						</Animated.View>

						<Animated.View 
							style={[
								styles.contentArea,
								{
									opacity: fadeAnim.content,
									transform: [{ translateX: slideAnim.content }]
								}
							]}
						>
							<Text style={[styles.headerText, isSmallMobile && { fontSize: 48, lineHeight: 56 }]}>Graduation Planning.{"\n"}Simplified.</Text>
							<Text style={styles.descriptionText}>Your One-Stop solution to create FREE Digital Graduation Announcements, Registries, and Share Events</Text>

							{/* Original Get Started button - only shown on non-mobile */}
							{!isMobile && (
								<View style={{ flexDirection: 'row', gap: 20, marginTop: 20, justifyContent: 'flex-start', alignItems: 'center' }}>
									<Pressable
										style={[
											styles.getStartedButton,
											hoveredButton === 'getStarted' && styles.getStartedButtonHover
										]}
										onMouseEnter={() => setHoveredButton('getStarted')}
										onMouseLeave={() => setHoveredButton(null)}
										onPress={() => handleLinkPress("Register")}
										onPressIn={handlePressIn}
										onPressOut={handlePressOut}
										activeOpacity={0.8}
									>
										<Text style={styles.buttonText}>Get Started</Text>
									</Pressable>
								</View>
							)}
						</Animated.View>

				</View>
			</View>
		</ScrollView>

		{/* Social Media Links */}
		<View style={styles.socialMediaContainer}>
			<Text style={styles.socialMediaText}>Follow us:</Text>
				<View style={styles.socialMediaLinks}>
					<TouchableOpacity 
						onPress={() => window.open('https://www.instagram.com/mytasselofficial', '_blank')}
						onMouseEnter={() => setHoveredSocial('instagram')}
						onMouseLeave={() => setHoveredSocial(null)}
						style={[
							styles.socialLink,
							hoveredSocial === 'instagram' && styles.socialLinkHover
						]}
					>
						<svg viewBox="0 0 24 24" width="20" height="20" fill="#fefde2">
							<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
						</svg>
					</TouchableOpacity>
					<TouchableOpacity 
						onPress={() => window.open('https://www.facebook.com/mytasselofficial', '_blank')}
						onMouseEnter={() => setHoveredSocial('facebook')}
						onMouseLeave={() => setHoveredSocial(null)}
						style={[
							styles.socialLink,
							hoveredSocial === 'facebook' && styles.socialLinkHover
						]}
					>
						<svg viewBox="0 0 24 24" width="20" height="20" fill="#fefde2">
							<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
						</svg>
					</TouchableOpacity>
					<TouchableOpacity 
						onPress={() => window.open('https://www.tiktok.com/@mytasselofficial', '_blank')}
						onMouseEnter={() => setHoveredSocial('tiktok')}
						onMouseLeave={() => setHoveredSocial(null)}
						style={[
							styles.socialLink,
							hoveredSocial === 'tiktok' && styles.socialLinkHover
						]}
					>
						<svg viewBox="0 0 24 24" width="20" height="20" fill="#fefde2">
							<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
						</svg>
					</TouchableOpacity>
			</View>
		</View>

		</View>
	);
};

export default Landing;
